import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import {
  createStore,
  deleteStore,
  getListStore,
  updateStore,
} from "../../api/store";
import { isNumeric } from "../../utils";

const useStoreMaster = () => {
  const [arrStores, setArrStores] = useState([]);
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [newStore, setNewStore] = useState({
    store_name: "",
    area: "",
    remarks: "",
  });

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [editStore, setEditStore] = useState(null);
  const [isPopupOpenDelete, setIsPopupOpenDelete] = useState(false);
  const [isPopupOpenUpdate, setIsPopupOpenUpdate] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [focusState, setFocusState] = useState({
    store_name: false,
    area: false,
  });

  const nameInputRef = useRef(null);

  const [errors, setErrors] = useState({
    store_name: "",
    area: "",
    remarks: "",
  });

  const [errorsUpdate, setErrorsUpdate] = useState({
    store_name: "",
    area: "",
    remarks: "",
  });

  const { data, refetch } = useQuery(
    ["stores", page, rowsPerPage],
    () => {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      return getListStore(params);
    },
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (data) {
      setArrStores(transformStoreData(data.data.data));
      setTotal(data.data.total);
    }
  }, [data]);

  useEffect(() => {
    setSortedData(arrStores.sort((a, b) => b.id - a.id));
  }, [arrStores]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const transformStoreData = (data) => {
    return data.map((store) => ({
      id: store.id.toString(),
      store_name: store.store_name,
      area: store.area,
      remarks: store.remarks || "",
    }));
  };

  const handleSort = (columnIndex) => {
    const isSorted = activeSortColumn === columnIndex;
    let direction = isSorted
      ? sortColumnDirection === "asc"
        ? "desc"
        : "asc"
      : "desc";
    sortedData.sort((a, b) => {
      let aValue = a[Object.keys(a)[columnIndex]];
      let bValue = b[Object.keys(b)[columnIndex]];

      // if aValue and bValue are numberic, sort parseInt
      if (isNumeric(aValue) && isNumeric(bValue)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData([...sortedData]);
    setActiveSortColumn(columnIndex);
    setSortColumnDirection(direction);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStore({ ...newStore, [name]: value });
    setErrors({ store_name: "", area: "", remarks: "" });
  };

  const handleAddStore = async () => {
    try {
      const newStoreData = {
        store_name: newStore.store_name,
        area: newStore.area,
        remarks: newStore.remarks,
      };

      const response = await createStore(newStoreData);

      if (response && response.data) {
        refetch();
        setNewStore({ store_name: "", area: "", remarks: "" });
        setErrors({ store_name: "", area: "", remarks: "" });
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrors((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
    }
  };

  const handleUpdateStore = async (updatedStore) => {
    try {
      const { id, store_name, area, remarks } = updatedStore;
      const updatedData = {
        store_name: store_name,
        area: area,
        remarks: remarks,
      };

      const response = await updateStore(id, updatedData);

      if (response && response.data) {
        refetch();
        return true
      } else {
        alert("エラーが発生しました。");
        return false
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrorsUpdate((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
      return false;
    }
  };

  const handleDeleteStore = async (id) => {
    try {
      const response = await deleteStore(id);

      if (response.success) {
        refetch();
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      alert("エラーが発生しました。");
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    const maxPage = Math.floor(total / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(Math.min(page, maxPage));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFocus = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleBlur = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: false,
    }));
  };

  const handleClosePopup = () => {
    setIsPopupOpenUpdate(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEditStore((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorsUpdate((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditClick = (store) => {
    setEditStore(store);
    setIsPopupOpenUpdate(true);
  };

  const handleDeleteClick = (store) => {
    setStoreToDelete(store);
    setIsPopupOpenDelete(true);
  };

  return {
    arrStores,
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    newStore,
    rowsPerPage,
    page,
    total,
    editStore,
    isPopupOpenDelete,
    isPopupOpenUpdate,
    storeToDelete,
    focusState,
    nameInputRef,
    errors,
    errorsUpdate,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handleInputChange,
    handleAddStore,
    handleUpdateStore,
    handleDeleteStore,
    handleFocus,
    handleBlur,
    handleClosePopup,
    handleChange,
    handleEditClick,
    handleDeleteClick,
    setIsPopupOpenDelete,
  };
};

export default useStoreMaster;
